<template>
  <div class="shadow-off d-flex flex-column gap-3" data-testid="gl-wise-materiality">
    <MaterialityFiltersHeader
      data-testid="gl-wise-materiality__filters-header"
      :reportingType.sync="localRuData.reporting_type"
      :periods.sync="localRuData.periods"
      :statementOptions="statementOptions"
      :minDate="minDate"
      :maxDate="maxDate"
      @removeMonth="$emit('removeMonth', $event)"
      @periods-changed="$emit('periods-changed', $event)"
      :isViewMode="isViewMode"
      :isCommentaryView.sync="localViewCycleType"
      :cycletypeReadview.sync="localViewModuleType"
    />
    <div class="gltable" v-if="!isViewMode" data-testid="gl-wise-materiality__edit-mode-section">
      <lb-datatablenew
        data-testid="gl-wise-materiality__edit-table"
        :headers="glHeaders"
        :items="formattedGLData"
        :perpage="itemsPerPage"
        :hidePagination="false"
        :enableslot="[
          'code',
          'edit_buttons',
          'datatypes',
          'control_thresholds_amount',
          'control_thresholds_percent',
          'floor_control_thresholds',
          'management_thresholds_amount',
          'management_thresholds_percent',
          'floor_management_thresholds',
          'control_thresholds_amount_regional',
          'control_thresholds_percent_regional',
          'floor_control_thresholds_regional',
          'management_thresholds_amount_regional',
          'management_thresholds_percent_regional',
          'floor_management_thresholds_regional',
        ]"
        :loading="loading"
        class="v-table-color-materiality"
      >
        <template v-slot:code="{ item }">
          <div class="d-flex align-center" data-testid="gl-wise-materiality__code-cell">
            <div :class="{ 'ml-5': item.__child }" data-testid="gl-wise-materiality__code-name">{{ item.code }} - {{ item.name }}</div>
            <v-btn icon color="" v-if="item.sub_ledger_exists" id="drill-down-subledger" class="ml-4" data-testid="gl-wise-materiality__drill-down-btn">
              <img src="/img/icons/DrillDown.svg" alt="drill down subledger image" data-testid="gl-wise-materiality__drill-down-icon" />
            </v-btn>
          </div>
        </template>
        <template v-slot:datatypes="">
          <div class="d-flex flex-column gap-5" data-testid="gl-wise-materiality__datatypes-cell">
          <div class="ellipsis-on" data-testid="gl-wise-materiality__datatype-proforma">vs {{ proformaLabel }}</div>
          <div class="ellipsis-on" data-testid="gl-wise-materiality__datatype-forecast">vs {{ forecastLabel }}</div>
          </div>
        </template>
        <template v-slot:edit_buttons="{ item }">
          <v-col v-if="glLevelEdit.enable" data-testid="gl-wise-materiality__edit-buttons-cell">
            <v-row data-testid="gl-wise-materiality__proforma-edit-row">
              <v-tooltip bottom content-class="tooltip-bottom" v-if="editableGLs.proforma.indexOf(item._id) === -1" data-testid="gl-wise-materiality__edit-proforma-tooltip">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon color="" @click="$emit('editGL', { id: item._id, type: 'proforma' })" id="edit-proforma-gl" data-testid="gl-wise-materiality__edit-proforma-btn">
                     <img 
                  src="/img/icons/PenEditIcon.svg"  
                  alt="Edit-Proforma"
                  data-testid="gl-wise-materiality__edit-proforma-icon" 
                />
                  </v-btn>
                </template>
                <span>Edit {{ proformaLabel }} {{ $nova.labelsName('materiality_page', 'commentary_gl_shortform_label', 'GL') }}</span>
              </v-tooltip>
              <v-tooltip bottom content-class="tooltip-bottom" v-else data-testid="gl-wise-materiality__cancel-proforma-tooltip">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon v-bind="attrs" v-on="on" color="" @click="$emit('cancelEditGL', { id: item._id, type: 'proforma' })" id="cancel-proforma-gl" data-testid="gl-wise-materiality__cancel-proforma-btn">
                    <v-icon data-testid="gl-wise-materiality__cancel-proforma-icon">mdi-close-circle-outline</v-icon>
                  </v-btn>
                </template>
                <span>Cancel</span>
              </v-tooltip>
            </v-row>
            <v-row data-testid="gl-wise-materiality__forecast-edit-row">
              <v-tooltip bottom content-class="tooltip-bottom" v-if="editableGLs.forecast.indexOf(item._id) === -1" data-testid="gl-wise-materiality__edit-forecast-tooltip">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon v-bind="attrs" v-on="on" color="" @click="$emit('editGL', { id: item._id, type: 'forecast' })" id="edit-forecast-gl" data-testid="gl-wise-materiality__edit-forecast-btn">
                    <img 
                  src="/img/icons/PenEditIcon.svg" 
                  alt="Edit-Forecast"
                  data-testid="gl-wise-materiality__edit-forecast-icon" 
                />
                  </v-btn>
                </template>
                <span>Edit {{ forecastLabel }} {{ $nova.labelsName('materiality_page', 'commentary_gl_shortform_label', 'GL') }}</span>
              </v-tooltip>
              <v-tooltip bottom content-class="tooltip-bottom" v-else data-testid="gl-wise-materiality__cancel-forecast-tooltip">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon v-bind="attrs" v-on="on" color="" @click="$emit('cancelEditGL', { id: item._id, type: 'forecast' })" id="cancel-forecast-gl" data-testid="gl-wise-materiality__cancel-forecast-btn">
                    <v-icon data-testid="gl-wise-materiality__cancel-forecast-icon">mdi-close-circle-outline</v-icon>
                  </v-btn>
                </template>
                <span>Cancel</span>
              </v-tooltip>
            </v-row>
          </v-col>
        </template>
        <template v-slot:control_thresholds_amount="{ item }">
          <div class="py-1" data-testid="gl-wise-materiality__control-amount-proforma-container">
            <lb-number
              data-testid="gl-wise-materiality__control-amount-proforma-input"
              v-model="glData[item._id].control_thresholds_amount_proforma"
              min="0"
              hidedetails
              :value="safeGLData(item._id).control_thresholds_amount_proforma || '0'"
              label=""
              :error="safeGLError(item._id).control_thresholds_amount_proforma"
              id="l_c_a_pro"
              class="cell-input"
              :disabled="editableGLs.proforma.indexOf(item._id) === -1 || !glData[item._id]"
            />
          </div>
          <div class="py-1" data-testid="gl-wise-materiality__control-amount-forecast-container">
            <lb-number
              data-testid="gl-wise-materiality__control-amount-forecast-input"
              v-model="glData[item._id].control_thresholds_amount_forecast"
              min="0"
              hidedetails
              :value="safeGLData(item._id).control_thresholds_amount_forecast || '0'"
              label=""
              :error="safeGLError(item._id).control_thresholds_amount_forecast"
              id="l_c_a_fore"
              class="cell-input"
              :disabled="editableGLs.forecast.indexOf(item._id) === -1 || !glData[item._id]"
            />
          </div>
        </template>
        <template v-slot:control_thresholds_percent="{ item }">
          <div class="py-1" data-testid="gl-wise-materiality__control-percent-proforma-container">
            <lb-number
              data-testid="gl-wise-materiality__control-percent-proforma-input"
              class="cell-input"
              v-if="editableGLs.proforma.indexOf(item._id) > -1 && glData[item._id]"
              v-model="glData[item._id].control_thresholds_percent_proforma"
              min="0"
              hidedetails
              :value="safeGLData(item._id).control_thresholds_percent_proforma || '0'"
              label=""
              :error="safeGLError(item._id).control_thresholds_percent_proforma"
              id="l_c_p_pro"
              :disabled="true"
            />
            <div class="text-right" id="l_c_p_pro" v-else data-testid="gl-wise-materiality__control-percent-proforma-display">
              <lb-number
              data-testid="gl-wise-materiality__control-percent-proforma-input-readonly"
              class="cell-input"
              v-model="glData[item._id].control_thresholds_percent_proforma"
              min="0"
              hidedetails
              :value="safeGLData(item._id).control_thresholds_percent_proforma || '0'"
              label=""
              :error="safeGLError(item._id).control_thresholds_percent_proforma"
              id="l_c_p_pro"
              :disabled="true"
            />
            </div>
          </div>
          <div class="py-1" data-testid="gl-wise-materiality__control-percent-forecast-container">
            <lb-number
              data-testid="gl-wise-materiality__control-percent-forecast-input"
              class="cell-input"
              v-model="glData[item._id].control_thresholds_percent_forecast"
              min="0"
              hidedetails
              :value="safeGLData(item._id).control_thresholds_percent_forecast || '0'"
              label=""
              :error="safeGLError(item._id).control_thresholds_percent_forecast"
              id="l_c_p_fore"
              :disabled="editableGLs.forecast.indexOf(item._id) === -1 || !glData[item._id]"
            />
          </div>
        </template>
        <template v-slot:management_thresholds_amount="{ item }">
          <div class="py-1" data-testid="gl-wise-materiality__management-amount-proforma-container">
            <lb-number
              data-testid="gl-wise-materiality__management-amount-proforma-input"
              class="cell-input"
              v-model="glData[item._id].management_thresholds_amount_proforma"
              min="0"
              hidedetails
              :value="safeGLData(item._id).management_thresholds_amount_proforma || '0'"
              label=""
              :error="safeGLError(item._id).management_thresholds_amount_proforma"
              id="l_m_a_pro"
              :disabled="editableGLs.proforma.indexOf(item._id) === -1 || !glData[item._id]"
            />
          </div>
          <div class="py-1" data-testid="gl-wise-materiality__management-amount-forecast-container">
            <lb-number
              data-testid="gl-wise-materiality__management-amount-forecast-input"
              class="cell-input"
              v-model="glData[item._id].management_thresholds_amount_forecast"
              min="0"
              hidedetails
              :value="safeGLData(item._id).management_thresholds_amount_forecast || '0'"
              label=""
              :error="safeGLError(item._id).management_thresholds_amount_forecast"
              id="l_m_a_fore"
              :disabled="editableGLs.forecast.indexOf(item._id) === -1 || !glData[item._id]"
            />
          </div>
        </template>
        <template v-slot:management_thresholds_percent="{ item }">
          <div class="py-1" data-testid="gl-wise-materiality__management-percent-proforma-container">
            <lb-number
              data-testid="gl-wise-materiality__management-percent-proforma-input"
              class="cell-input"
              v-model="glData[item._id].management_thresholds_percent_proforma"
              min="0"
              hidedetails
              :value="safeGLData(item._id).management_thresholds_percent_proforma || '0'"
              label=""
              :error="safeGLError(item._id).management_thresholds_percent_proforma"
              id="l_m_p_pro"
              :disabled="editableGLs.proforma.indexOf(item._id) === -1 || !glData[item._id]"
            />
          </div>
          <div class="py-1" data-testid="gl-wise-materiality__management-percent-forecast-container">
            <lb-number
              data-testid="gl-wise-materiality__management-percent-forecast-input"
              class="cell-input"
              v-model="glData[item._id].management_thresholds_percent_forecast"
              min="0"
              hidedetails
              :value="safeGLData(item._id).management_thresholds_percent_forecast || '0'"
              label=""
              :error="safeGLError(item._id).management_thresholds_percent_forecast"
              id="l_m_p_fore"
              :disabled="editableGLs.forecast.indexOf(item._id) === -1 || !glData[item._id]"
            />
          </div>
        </template>
        <template v-slot:control_thresholds_amount_regional="{ item }">
          <div class="py-1" data-testid="gl-wise-materiality__regional-control-amount-proforma-container">
            <lb-number
              data-testid="gl-wise-materiality__regional-control-amount-proforma-input"
              class="cell-input"
              v-model="glData[item._id].control_thresholds_amount_regional_proforma"
              min="0"
              hidedetails
              :value="safeGLData(item._id).control_thresholds_amount_regional_proforma || '0'"
              label=""
              :error="safeGLError(item._id).control_thresholds_amount_regional_proforma"
              id="r_c_a_pro"
              :disabled="editableGLs.proforma.indexOf(item._id) === -1 || !glData[item._id]"
            />
          </div>
          <div class="py-1" data-testid="gl-wise-materiality__regional-control-amount-forecast-container">
            <lb-number
              data-testid="gl-wise-materiality__regional-control-amount-forecast-input"
              class="cell-input"
              v-model="glData[item._id].control_thresholds_amount_regional_forecast"
              min="0"
              hidedetails
              :value="safeGLData(item._id).control_thresholds_amount_regional_forecast || '0'"
              label=""
              :error="safeGLError(item._id).control_thresholds_amount_regional_forecast"
              id="r_c_a_fore"
              :disabled="editableGLs.forecast.indexOf(item._id) === -1 || !glData[item._id]"
            />
          </div>
        </template>
        <template v-slot:control_thresholds_percent_regional="{ item }">
          <div class="py-1" data-testid="gl-wise-materiality__regional-control-percent-proforma-container">
            <lb-number
              data-testid="gl-wise-materiality__regional-control-percent-proforma-input"
              class="cell-input"
              v-if="editableGLs.proforma.indexOf(item._id) > -1 && glData[item._id]"
              v-model="glData[item._id].control_thresholds_percent_regional_proforma"
              min="0"
              hidedetails
              :value="safeGLData(item._id).control_thresholds_percent_regional_proforma || '0'"
              label=""
              :error="safeGLError(item._id).control_thresholds_percent_regional_proforma"
              id="r_c_p_pro"
              :disabled="true"
            />
            <div class="text-right" id="r_c_p_pro" v-else data-testid="gl-wise-materiality__regional-control-percent-proforma-display">
              <lb-number
              data-testid="gl-wise-materiality__regional-control-percent-proforma-input-readonly"
              class="cell-input"
              v-model="glData[item._id].control_thresholds_percent_regional_proforma"
              min="0"
              hidedetails
              :value="safeGLData(item._id).control_thresholds_percent_regional_proforma || '0'"
              label=""
              :error="safeGLError(item._id).control_thresholds_percent_regional_proforma"
              id="r_c_p_pro"
              :disabled="true"
            />
            </div>
          </div>
          <div class="py-1" data-testid="gl-wise-materiality__regional-control-percent-forecast-container">
            <lb-number
              data-testid="gl-wise-materiality__regional-control-percent-forecast-input"
              class="cell-input"
              v-model="glData[item._id].control_thresholds_percent_regional_forecast"
              min="0"
              hidedetails
              :value="safeGLData(item._id).control_thresholds_percent_regional_forecast || '0'"
              label=""
              :error="safeGLError(item._id).control_thresholds_percent_regional_forecast"
              id="r_c_p_fore"
              :disabled="editableGLs.forecast.indexOf(item._id) === -1 || !glData[item._id]"
            />
          </div>
        </template>
        <template v-slot:management_thresholds_amount_regional="{ item }">
          <div class="py-1" data-testid="gl-wise-materiality__regional-management-amount-proforma-container">
            <lb-number
              data-testid="gl-wise-materiality__regional-management-amount-proforma-input"
              class="cell-input"
              v-model="glData[item._id].management_thresholds_amount_regional_proforma"
              min="0"
              hidedetails
              :value="safeGLData(item._id).management_thresholds_amount_regional_proforma || '0'"
              label=""
              :error="safeGLError(item._id).management_thresholds_amount_regional_proforma"
              id="r_m_a_pro"
              :disabled="editableGLs.proforma.indexOf(item._id) === -1 || !glData[item._id]"
            />
          </div>
          <div class="py-1" data-testid="gl-wise-materiality__regional-management-amount-forecast-container">
            <lb-number
              data-testid="gl-wise-materiality__regional-management-amount-forecast-input"
              class="cell-input"
              v-model="glData[item._id].management_thresholds_amount_regional_forecast"
              min="0"
              hidedetails
              :value="safeGLData(item._id).management_thresholds_amount_regional_forecast || '0'"
              label=""
              :error="safeGLError(item._id).management_thresholds_amount_regional_forecast"
              id="r_m_a_fore"
              :disabled="editableGLs.forecast.indexOf(item._id) === -1 || !glData[item._id]"
            />
          </div>
        </template>
        <template v-slot:management_thresholds_percent_regional="{ item }">
          <div class="py-1" data-testid="gl-wise-materiality__regional-management-percent-proforma-container">
            <lb-number
              data-testid="gl-wise-materiality__regional-management-percent-proforma-input"
              class="cell-input"
              v-model="glData[item._id].management_thresholds_percent_regional_proforma"
              min="0"
              hidedetails
              :value="safeGLData(item._id).management_thresholds_percent_regional_proforma || '0'"
              label=""
              :error="safeGLError(item._id).management_thresholds_percent_regional_proforma"
              id="r_m_p_pro"
              :disabled="editableGLs.proforma.indexOf(item._id) === -1 || !glData[item._id]"
            />
          </div>
          <div class="py-1" data-testid="gl-wise-materiality__regional-management-percent-forecast-container">
            <lb-number
              data-testid="gl-wise-materiality__regional-management-percent-forecast-input"
              class="cell-input"
              v-model="glData[item._id].management_thresholds_percent_regional_forecast"
              min="0"
              hidedetails
              :value="safeGLData(item._id).management_thresholds_percent_regional_forecast || '0'"
              label=""
              :error="safeGLError(item._id).management_thresholds_percent_regional_forecast"
              id="r_m_p_fore"
              :disabled="editableGLs.forecast.indexOf(item._id) === -1 || !glData[item._id]"
            />
          </div>
        </template>
      </lb-datatablenew>
    </div>
    <div v-else data-testid="gl-wise-materiality__view-mode-section">
       <lb-datatablenew
        data-testid="gl-wise-materiality__view-table"
        :headers="filteredGlHeaders"
        :items="viewGLLevelMaterialityData"
        :perpage="itemsPerPage"
        :hidePagination="false"
        :enableslot="[
          'code',
          'name',
          'edit_buttons',
          'datatypes',
          'control_thresholds_amount',
          'control_thresholds_percent',
          'floor_control_thresholds',
          'management_thresholds_amount',
          'management_thresholds_percent',
          'floor_management_thresholds',
          'control_thresholds_amount_regional',
          'control_thresholds_percent_regional',
          'floor_control_thresholds_regional',
          'management_thresholds_amount_regional',
          'management_thresholds_percent_regional',
          'floor_management_thresholds_regional',
        ]"
        :loading="loading"
      >
       <template v-slot:code="{ item }">
          <div class="d-flex align-center" data-testid="gl-wise-materiality__view-code-cell">
            <div :class="{ 'ml-5': item.__child }" data-testid="gl-wise-materiality__view-code-name">{{ item.code }} - {{ item.name }}</div>
            <v-btn icon color="" v-if="item.sub_ledger_exists" id="drill-down-subledger" data-testid="gl-wise-materiality__view-drill-down-btn">
              <img src="/img/icons/DrillDown.svg" alt="drill down subledger image" data-testid="gl-wise-materiality__view-drill-down-icon" />
            </v-btn>
          </div>
        </template>
        
        <template v-slot:name="{ item }">
          <div class="d-flex align-center" data-testid="gl-wise-materiality__view-name-cell">
            <div class="mr-0" data-testid="gl-wise-materiality__view-name-text">{{ item.name }}</div>
          </div>
        </template>
        <template v-slot:datatypes="">
          <div class="ellipsis-on" data-testid="gl-wise-materiality__view-datatype-proforma">vs {{ proformaLabel }}</div>
          <div class="ellipsis-on" data-testid="gl-wise-materiality__view-datatype-forecast">vs {{ forecastLabel }}</div>
        </template>
        <template v-slot:edit_buttons="{ item }">
          <v-col v-if="glLevelEdit.enable" data-testid="gl-wise-materiality__view-edit-buttons-cell">
            <v-row data-testid="gl-wise-materiality__view-proforma-edit-row">
              <v-tooltip bottom content-class="tooltip-bottom" v-if="editableGLs.proforma.indexOf(item._id) === -1" data-testid="gl-wise-materiality__view-edit-proforma-tooltip">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon v-bind="attrs" v-on="on" color="" @click="$emit('editGL', { id: item._id, type: 'proforma' })" id="edit-proforma-gl" data-testid="gl-wise-materiality__view-edit-proforma-btn">
                    <EditIcon type="proforma" data-testid="gl-wise-materiality__view-edit-proforma-icon" />
                  </v-btn>
                </template>
                <span>Edit Proforma GL</span>
              </v-tooltip>
              <v-tooltip bottom content-class="tooltip-bottom" v-else data-testid="gl-wise-materiality__view-cancel-proforma-tooltip">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon v-bind="attrs" v-on="on" color="" @click="$emit('cancelEditGL', { id: item._id, type: 'proforma' })" id="cancel-proforma-gl" data-testid="gl-wise-materiality__view-cancel-proforma-btn">
                    <v-icon data-testid="gl-wise-materiality__view-cancel-proforma-icon">mdi-close-circle-outline</v-icon>
                  </v-btn>
                </template>
                <span>Cancel</span>
              </v-tooltip>
            </v-row>
            <v-row data-testid="gl-wise-materiality__view-forecast-edit-row">
              <v-tooltip bottom content-class="tooltip-bottom" v-if="editableGLs.forecast.indexOf(item._id) === -1" data-testid="gl-wise-materiality__view-edit-forecast-tooltip">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon v-bind="attrs" v-on="on" color="" @click="$emit('editGL', { id: item._id, type: 'forecast' })" id="edit-forecast-gl" data-testid="gl-wise-materiality__view-edit-forecast-btn">
                    <EditIcon type="forecast" data-testid="gl-wise-materiality__view-edit-forecast-icon" />
                  </v-btn>
                </template>
                <span>Edit {{ forecastLabel }} GL</span>
              </v-tooltip>
              <v-tooltip bottom content-class="tooltip-bottom" v-else data-testid="gl-wise-materiality__view-cancel-forecast-tooltip">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon v-bind="attrs" v-on="on" color="" @click="$emit('cancelEditGL', { id: item._id, type: 'forecast' })" id="cancel-forecast-gl" data-testid="gl-wise-materiality__view-cancel-forecast-btn">
                    <v-icon data-testid="gl-wise-materiality__view-cancel-forecast-icon">mdi-close-circle-outline</v-icon>
                  </v-btn>
                </template>
                <span>Cancel</span>
              </v-tooltip>
            </v-row>
          </v-col>
        </template>
        <template v-slot:control_thresholds_amount="{ item }">
          <div class="cell-input" data-testid="gl-wise-materiality__view-control-amount-proforma-container">
            <lb-number
              data-testid="gl-wise-materiality__view-control-amount-proforma-input"
              v-if="editableGLs.proforma.indexOf(item._id) > -1"
              v-model="item.control_thresholds_amount_proforma"
              min="0"
              hidedetails
              :value="item.control_thresholds_amount_proforma || '0'"
              label=""
              :error="glError[item._id].control_thresholds_amount_proforma"
              id="l_c_a_pro"
              class="cell-input"
            />
            <div class="text-right" id="l_c_a_pro" v-else data-testid="gl-wise-materiality__view-control-amount-proforma-display">
              <span v-if="item.control_thresholds_amount_proforma !== 0.001" data-testid="gl-wise-materiality__view-control-amount-proforma-value">{{ item.control_thresholds_amount_proforma || 0 }}</span>
              <span v-else data-testid="gl-wise-materiality__view-control-amount-proforma-empty"> - </span>
            </div>
          </div>
          <div class="cell-input" data-testid="gl-wise-materiality__view-control-amount-forecast-container">
            <lb-number
              data-testid="gl-wise-materiality__view-control-amount-forecast-input"
              v-if="editableGLs.forecast.indexOf(item._id) > -1"
              v-model="item.control_thresholds_amount_forecast"
              min="0"
              hidedetails
              :value="item.control_thresholds_amount_forecast || '0'"
              label=""
              :error="glError[item._id].control_thresholds_amount_forecast"
              id="l_c_a_fore"
            />
            <div class="text-right" id="l_c_a_fore" v-else data-testid="gl-wise-materiality__view-control-amount-forecast-display">
              <span v-if="item.control_thresholds_amount_forecast !== 0.001" data-testid="gl-wise-materiality__view-control-amount-forecast-value">{{ item.control_thresholds_amount_forecast || 0 }}</span>
              <span v-else data-testid="gl-wise-materiality__view-control-amount-forecast-empty"> - </span>
            </div>
          </div>
        </template>
        <template v-slot:control_thresholds_percent="{ item }">
          <div class="cell-input" data-testid="gl-wise-materiality__view-control-percent-proforma-container">
            <lb-number
              data-testid="gl-wise-materiality__view-control-percent-proforma-input"
              v-if="editableGLs.proforma.indexOf(item._id) > -1"
              v-model="item.control_thresholds_percent_proforma"
              min="0"
              hidedetails
              :value="item.control_thresholds_percent_proforma || '0'"
              label=""
              :error="glError[item._id].control_thresholds_percent_proforma"
              id="l_c_p_pro"
              :disabled="true"
            />
            <div class="text-right" id="l_c_p_pro" v-else data-testid="gl-wise-materiality__view-control-percent-proforma-display">
              <span v-if="item.control_thresholds_percent_proforma !== 0.001" data-testid="gl-wise-materiality__view-control-percent-proforma-value">{{ item.control_thresholds_percent_proforma || 0 }}%</span>
              <span v-else data-testid="gl-wise-materiality__view-control-percent-proforma-empty"> - </span>
            </div>
          </div>
          <div class="cell-input" data-testid="gl-wise-materiality__view-control-percent-forecast-container">
            <lb-number
              data-testid="gl-wise-materiality__view-control-percent-forecast-input"
              v-if="editableGLs.forecast.indexOf(item._id) > -1"
              v-model="item.control_thresholds_percent_forecast"
              min="0"
              hidedetails
              :value="item.control_thresholds_percent_forecast || '0'"
              label=""
              :error="glError[item._id].control_thresholds_percent_forecast"
              id="l_c_p_fore"
            />
            <div class="text-right" id="l_c_p_fore" v-else data-testid="gl-wise-materiality__view-control-percent-forecast-display">
              <span v-if="item.control_thresholds_percent_forecast !== 0.001" data-testid="gl-wise-materiality__view-control-percent-forecast-value">{{ item.control_thresholds_percent_forecast || 0 }}%</span>
              <span v-else data-testid="gl-wise-materiality__view-control-percent-forecast-empty"> - </span>
            </div>
          </div>
        </template>
        <template v-slot:management_thresholds_amount="{ item }">
          <div class="cell-input" data-testid="gl-wise-materiality__view-management-amount-proforma-container">
            <lb-number
              data-testid="gl-wise-materiality__view-management-amount-proforma-input"
              v-if="editableGLs.proforma.indexOf(item._id) > -1"
              v-model="item.management_thresholds_amount_proforma"
              min="0"
              hidedetails
              :value="item.management_thresholds_amount_proforma || '0'"
              label=""
              :error="glError[item._id].management_thresholds_amount_proforma"
              id="l_m_a_pro"
            />
            <div class="text-right" id="l_m_a_pro" v-else data-testid="gl-wise-materiality__view-management-amount-proforma-display">
              <span v-if="item.management_thresholds_amount_proforma !== 0.001" data-testid="gl-wise-materiality__view-management-amount-proforma-value">{{ item.management_thresholds_amount_proforma || 0 }}</span>
              <span v-else data-testid="gl-wise-materiality__view-management-amount-proforma-empty"> - </span>
            </div>
          </div>
          <div class="cell-input" data-testid="gl-wise-materiality__view-management-amount-forecast-container">
            <lb-number
              data-testid="gl-wise-materiality__view-management-amount-forecast-input"
              v-if="editableGLs.forecast.indexOf(item._id) > -1"
              v-model="item.management_thresholds_amount_forecast"
              min="0"
              hidedetails
              :value="item.management_thresholds_amount_forecast || '0'"
              label=""
              :error="glError[item._id].management_thresholds_amount_forecast"
              id="l_m_a_fore"
            />
            <div class="text-right" id="l_m_a_fore" v-else data-testid="gl-wise-materiality__view-management-amount-forecast-display">
              <span v-if="item.management_thresholds_amount_forecast !== 0.001" data-testid="gl-wise-materiality__view-management-amount-forecast-value">{{ item.management_thresholds_amount_forecast || 0 }}</span>
              <span v-else data-testid="gl-wise-materiality__view-management-amount-forecast-empty"> - </span>
            </div>
          </div>
        </template>
        <template v-slot:management_thresholds_percent="{ item }">
          <div class="cell-input" data-testid="gl-wise-materiality__view-management-percent-proforma-container">
            <lb-number
              data-testid="gl-wise-materiality__view-management-percent-proforma-input"
              v-if="editableGLs.proforma.indexOf(item._id) > -1"
              v-model="item.management_thresholds_percent_proforma"
              min="0"
              hidedetails
              :value="item.management_thresholds_percent_proforma || '0'"
              label=""
              :error="glError[item._id].management_thresholds_percent_proforma"
              id="l_m_p_pro"
            />
            <div class="text-right" v-else data-testid="gl-wise-materiality__view-management-percent-proforma-display">
              <span v-if="item.management_thresholds_percent_proforma !== 0.001" data-testid="gl-wise-materiality__view-management-percent-proforma-value">{{ item.management_thresholds_percent_proforma || 0 }}%</span>
              <span v-else data-testid="gl-wise-materiality__view-management-percent-proforma-empty"> - </span>
            </div>
          </div>
          <div class="cell-input" data-testid="gl-wise-materiality__view-management-percent-forecast-container">
            <lb-number
              data-testid="gl-wise-materiality__view-management-percent-forecast-input"
              v-if="editableGLs.forecast.indexOf(item._id) > -1"
              v-model="item.management_thresholds_percent_forecast"
              min="0"
              hidedetails
              :value="item.management_thresholds_percent_forecast || '0'"
              label=""
              :error="glError[item._id].management_thresholds_percent_forecast"
              id="l_m_p_fore"
            />
            <div class="text-right" v-else data-testid="gl-wise-materiality__view-management-percent-forecast-display">
              <span v-if="item.management_thresholds_percent_forecast !== 0.001" data-testid="gl-wise-materiality__view-management-percent-forecast-value">{{ item.management_thresholds_percent_forecast || 0 }}%</span>
              <span v-else data-testid="gl-wise-materiality__view-management-percent-forecast-empty"> - </span>
            </div>
          </div>
        </template>
        <template v-slot:control_thresholds_amount_regional="{ item }">
          <div class="cell-input" data-testid="gl-wise-materiality__view-regional-control-amount-proforma-container">
            <lb-number
              data-testid="gl-wise-materiality__view-regional-control-amount-proforma-input"
              v-if="editableGLs.proforma.indexOf(item._id) > -1"
              v-model="item.control_thresholds_amount_regional_proforma"
              min="0"
              hidedetails
              :value="item.control_thresholds_amount_regional_proforma || '0'"
              label=""
              :error="glError[item._id].control_thresholds_amount_regional_proforma"
              id="r_c_a_pro"
            />
            <div class="text-right" id="r_c_a_pro" v-else data-testid="gl-wise-materiality__view-regional-control-amount-proforma-display">
              <span v-if="item.control_thresholds_amount_regional_proforma !== 0.001" data-testid="gl-wise-materiality__view-regional-control-amount-proforma-value">{{ item.control_thresholds_amount_regional_proforma || 0 }}</span>
              <span v-else data-testid="gl-wise-materiality__view-regional-control-amount-proforma-empty"> - </span>
            </div>
          </div>
          <div class="cell-input" data-testid="gl-wise-materiality__view-regional-control-amount-forecast-container">
            <lb-number
              data-testid="gl-wise-materiality__view-regional-control-amount-forecast-input"
              v-if="editableGLs.forecast.indexOf(item._id) > -1"
              v-model="item.control_thresholds_amount_regional_forecast"
              min="0"
              hidedetails
              :value="item.control_thresholds_amount_regional_forecast || '0'"
              label=""
              :error="glError[item._id].control_thresholds_amount_regional_forecast"
              id="r_c_a_fore"
            />
            <div class="text-right" id="r_c_a_fore" v-else data-testid="gl-wise-materiality__view-regional-control-amount-forecast-display">
              <span v-if="item.control_thresholds_amount_regional_forecast !== 0.001" data-testid="gl-wise-materiality__view-regional-control-amount-forecast-value">{{ item.control_thresholds_amount_regional_forecast || 0 }}</span>
              <span v-else data-testid="gl-wise-materiality__view-regional-control-amount-forecast-empty"> - </span>
            </div>
          </div>
        </template>
        <template v-slot:control_thresholds_percent_regional="{ item }">
          <div class="cell-input" data-testid="gl-wise-materiality__view-regional-control-percent-proforma-container">
            <lb-number
              data-testid="gl-wise-materiality__view-regional-control-percent-proforma-input"
              v-if="editableGLs.proforma.indexOf(item._id) > -1"
              v-model="item.control_thresholds_percent_regional_proforma"
              min="0"
              hidedetails
              :value="item.control_thresholds_percent_regional_proforma || '0'"
              label=""
              :error="glError[item._id].control_thresholds_percent_regional_proforma"
              id="r_c_p_pro"
              :disabled="true"
            />
            <div class="text-right" id="r_c_p_pro" v-else data-testid="gl-wise-materiality__view-regional-control-percent-proforma-display">
              <span v-if="item.control_thresholds_percent_regional_proforma !== 0.001" data-testid="gl-wise-materiality__view-regional-control-percent-proforma-value">{{ item.control_thresholds_percent_regional_proforma || 0 }}%</span>
              <span v-else data-testid="gl-wise-materiality__view-regional-control-percent-proforma-empty"> - </span>
            </div>
          </div>
          <div class="cell-input" data-testid="gl-wise-materiality__view-regional-control-percent-forecast-container">
            <lb-number
              data-testid="gl-wise-materiality__view-regional-control-percent-forecast-input"
              v-if="editableGLs.forecast.indexOf(item._id) > -1"
              v-model="item.control_thresholds_percent_regional_forecast"
              min="0"
              hidedetails
              :value="item.control_thresholds_percent_regional_forecast || '0'"
              label=""
              :error="glError[item._id].control_thresholds_percent_regional_forecast"
              id="r_c_p_fore"
            />
            <div class="text-right" id="r_c_p_fore" v-else data-testid="gl-wise-materiality__view-regional-control-percent-forecast-display">
              <span v-if="item.control_thresholds_percent_regional_forecast !== 0.001" data-testid="gl-wise-materiality__view-regional-control-percent-forecast-value">{{ item.control_thresholds_percent_regional_forecast || 0 }}%</span>
              <span v-else data-testid="gl-wise-materiality__view-regional-control-percent-forecast-empty"> - </span>
            </div>
          </div>
        </template>
        <template v-slot:management_thresholds_amount_regional="{ item }">
          <div class="cell-input" data-testid="gl-wise-materiality__view-regional-management-amount-proforma-container">
            <lb-number
              data-testid="gl-wise-materiality__view-regional-management-amount-proforma-input"
              v-if="editableGLs.proforma.indexOf(item._id) > -1"
              v-model="item.management_thresholds_amount_regional_proforma"
              min="0"
              hidedetails
              :value="item.management_thresholds_amount_regional_proforma || '0'"
              label=""
              :error="glError[item._id].management_thresholds_amount_regional_proforma"
              id="r_m_a_pro"
            />
            <div class="text-right" id="r_m_a_pro" v-else data-testid="gl-wise-materiality__view-regional-management-amount-proforma-display">
              <span v-if="item.management_thresholds_amount_regional_proforma !== 0.001" data-testid="gl-wise-materiality__view-regional-management-amount-proforma-value">{{ item.management_thresholds_amount_regional_proforma || 0 }}</span>
              <span v-else data-testid="gl-wise-materiality__view-regional-management-amount-proforma-empty"> - </span>
            </div>
          </div>
          <div class="cell-input" data-testid="gl-wise-materiality__view-regional-management-amount-forecast-container">
            <lb-number
              data-testid="gl-wise-materiality__view-regional-management-amount-forecast-input"
              v-if="editableGLs.forecast.indexOf(item._id) > -1"
              v-model="item.management_thresholds_amount_regional_forecast"
              min="0"
              hidedetails
              :value="item.management_thresholds_amount_regional_forecast || '0'"
              label=""
              :error="glError[item._id].management_thresholds_amount_regional_forecast"
              id="r_m_a_fore"
            />
            <div class="text-right" id="r_m_a_fore" v-else data-testid="gl-wise-materiality__view-regional-management-amount-forecast-display">
              <span v-if="item.management_thresholds_amount_regional_forecast !== 0.001" data-testid="gl-wise-materiality__view-regional-management-amount-forecast-value">{{ item.management_thresholds_amount_regional_forecast || 0 }}</span>
              <span v-else data-testid="gl-wise-materiality__view-regional-management-amount-forecast-empty"> - </span>
            </div>
          </div>
        </template>
        <template v-slot:management_thresholds_percent_regional="{ item }">
          <div class="cell-input" data-testid="gl-wise-materiality__view-regional-management-percent-proforma-container">
            <lb-number
              data-testid="gl-wise-materiality__view-regional-management-percent-proforma-input"
              v-if="editableGLs.proforma.indexOf(item._id) > -1"
              v-model="item.management_thresholds_percent_regional_proforma"
              min="0"
              hidedetails
              :value="item.management_thresholds_percent_regional_proforma || '0'"
              label=""
              :error="glError[item._id].management_thresholds_percent_regional_proforma"
              id="r_m_p_pro"
            />
            <div class="text-right" id="r_m_p_pro" v-else data-testid="gl-wise-materiality__view-regional-management-percent-proforma-display">
              <span v-if="item.management_thresholds_percent_regional_proforma !== 0.001" data-testid="gl-wise-materiality__view-regional-management-percent-proforma-value">{{ item.management_thresholds_percent_regional_proforma || 0 }}%</span>
              <span v-else data-testid="gl-wise-materiality__view-regional-management-percent-proforma-empty"> - </span>
            </div>
          </div>
          <div class="cell-input" data-testid="gl-wise-materiality__view-regional-management-percent-forecast-container">
            <lb-number
              data-testid="gl-wise-materiality__view-regional-management-percent-forecast-input"
              v-if="editableGLs.forecast.indexOf(item._id) > -1"
              v-model="item.management_thresholds_percent_regional_forecast"
              min="0"
              hidedetails
              :value="item.management_thresholds_percent_regional_forecast || '0'"
              label=""
              :error="glError[item._id].management_thresholds_percent_regional_forecast"
              id="r_m_p_fore"
            />
            <div class="text-right" id="r_m_p_fore" v-else data-testid="gl-wise-materiality__view-regional-management-percent-forecast-display">
              <span v-if="item.management_thresholds_percent_regional_forecast !== 0.001" data-testid="gl-wise-materiality__view-regional-management-percent-forecast-value">{{ item.management_thresholds_percent_regional_forecast || 0 }}%</span>
              <span v-else data-testid="gl-wise-materiality__view-regional-management-percent-forecast-empty"> - </span>
            </div>
          </div>
        </template>
      </lb-datatablenew>
    </div>
  </div>
</template>

<script>

import MaterialityFiltersHeader from './MaterialityFiltersHeader.vue';

export default {
  name: 'GLWiseMateriality',
  components: {
    MaterialityFiltersHeader,
   // MaterialityPagination,
  },
   data() {
    return {
      selectedStatementType: 'pl',
      statementOptions: [{labelName: 'Profit & Loss',name: 'pl'}, {labelName: 'Balance Sheet', name: 'bs'}],
      itemsPerPage: 10,
    };
  },
  props: {
    ruData: {
      type: Object,
      default: () => ({}),
    },
    glHeaders: {
      type: Array,
      default: () => [],
    },
    formattedGLData: {
      type: Array,
      default: () => [],
    },
    glData: {
      type: Object,
      default: () => ({}),
    },
    glError: {
      type: Object,
      default: () => ({}),
    },
    editableGLs: {
      type: Object,
      default: () => ({ proforma: [], forecast: [] }),
    },
    loading: {
      type: Boolean,
      default: false,
    },
    minDate: {
      type: String,
      default: '',
    },
    maxDate: {
      type: String,
      default: '',
    },
    multipleMaterialityToggle: {
      type: Boolean,
      default: false,
    },
    glLevelEdit: {
      type: Object,
      default: () => ({ enable: false }),
    },
    isViewMode: {
      type: Boolean,
      default: false,
    },
    viewGLLevelMaterialityData: {
      type: Array,
      default: () => [],
    },
    // View mode filter state props (to preserve user selections)
    viewCycleType: {
      type: String,
      default: 'actual'
    },
    viewModuleType: {
      type: String,
      default: 'pl'
    },
  },
  computed: {
    localRuData: {
      get() {
        return this.ruData;
      },
      set(val) {
        this.$emit('update:ruData', val);
      },
    },
    /**
     * Local view cycle type with two-way binding to parent
     */
    localViewCycleType: {
      get() {
        return this.viewCycleType;
      },
      set(val) {
        this.$emit('update:viewCycleType', val);
      },
    },
    /**
     * Local view module type with two-way binding to parent
     */
    localViewModuleType: {
      get() {
        return this.viewModuleType;
      },
      set(val) {
        this.$emit('update:viewModuleType', val);
      },
    },
    /**
     * Safely access GL data with default values to prevent undefined errors
     * @param {string} itemId - The GL item ID
     * @returns {object} GL data object with all threshold properties
     */
    safeGLData() {
      return (itemId) => {
        if (!this.glData || !itemId || !this.glData[itemId]) {
          // Return default structure if data is missing
          return {
            control_thresholds_amount_proforma: 0,
            control_thresholds_percent_proforma: 0,
            management_thresholds_amount_proforma: 0,
            management_thresholds_percent_proforma: 0,
            control_thresholds_amount_regional_proforma: 0,
            control_thresholds_percent_regional_proforma: 0,
            management_thresholds_amount_regional_proforma: 0,
            management_thresholds_percent_regional_proforma: 0,
            control_thresholds_amount_forecast: 0,
            control_thresholds_percent_forecast: 0,
            management_thresholds_amount_forecast: 0,
            management_thresholds_percent_forecast: 0,
            control_thresholds_amount_regional_forecast: 0,
            control_thresholds_percent_regional_forecast: 0,
            management_thresholds_amount_regional_forecast: 0,
            management_thresholds_percent_regional_forecast: 0,
          };
        }
        return this.glData[itemId];
      };
    },
    /**
     * Safely access GL error data with default values
     * @param {string} itemId - The GL item ID
     * @returns {object} GL error object
     */
    safeGLError() {
      return (itemId) => {
        if (!this.glError || !itemId || !this.glError[itemId]) {
          return {};
        }
        return this.glError[itemId];
      };
    },
    /**
     * Filter headers to hide edit_buttons column in view mode
     */
    filteredGlHeaders() {
      if (this.isViewMode) {
        return this.glHeaders.filter(header => header.value !== 'edit_buttons');
      }
      return this.glHeaders;
    },
    /**
     * Dynamic label for Actual based on reporting_type
     * Returns balance sheet specific label when type is 'bs', otherwise default
     */
    actualLabel() {
      return this.localRuData.reporting_type === 'bs'
        ? this.$nova.labelsName('cycle_page', 'actual_label_balancesheet', 'Actual')
        : this.$nova.labelsName('cycle_page', 'actual_label', 'Actual');
    },
    /**
     * Dynamic label for Proforma based on reporting_type
     * Returns balance sheet specific label when type is 'bs', otherwise default
     */
    proformaLabel() {
      return this.localRuData.reporting_type === 'bs'
        ? this.$nova.labelsName('cycle_page', 'proforma_shortform_label_balancesheet', 'Proforma')
        : this.$nova.labelsName('cycle_page', 'proforma_shortform_label', 'Proforma');
    },
    /**
     * Dynamic label for Forecast based on reporting_type
     * Returns balance sheet specific label when type is 'bs', otherwise default
     */
    forecastLabel() {
      return this.localRuData.reporting_type === 'bs'
        ? this.$nova.labelsName('cycle_page', 'forecast_shortform_label_balancesheet', 'Forecast')
        : this.$nova.labelsName('cycle_page', 'forecast_shortform_label', 'Forecast');
    },
  },
   watch:{
    'localRuData.periods'(newVal, oldVal){
      if (this.isViewMode) {
      if (newVal && newVal.length > 0 && JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
        this.$emit('periods-changed', newVal);
      }
    }
  }
},
  methods: {
  },
};
</script>

<style scoped>
.selectmonth {
  color: #828285;
}
.ellipsis-on {

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.icon-size-16 {
  width: 16px;
  height: 16px;
}
.v-table-color-materiality  tr td {
  padding: 0px 4px !important;
}
.chip-gray-close >>> .v-chip__close {
  color: #828285 !important;
}
.pageination-theme >>> .v-pagination__item {
  background-color: var(--grey-50);
}
</style>
